import { Injectable } from '@angular/core';
import { PropertyBasicInfo, UnitType, PricingRules, Season } from '../models';
import { WIZARD_LIMITS } from '../config/wizard-limits';
import { stripHtmlTags } from '../utils/form-validators';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface UnitTypeValidationMap {
  [unitId: string]: string[];
}

@Injectable()
export class WizardValidationService {

  validateBasicInfo(info: PropertyBasicInfo | null): ValidationResult {
    const errors: string[] = [];

    if (!info) {
      return { isValid: false, errors: ['Dane podstawowe są wymagane'] };
    }

    if (!info.name || info.name.length < 3 || info.name.length > 100) {
      errors.push('Nazwa musi mieć od 3 do 100 znaków');
    }

    const descriptionTextLength = stripHtmlTags(info.description || '').length;
    if (!info.description || descriptionTextLength < 50 || descriptionTextLength > 5000) {
      errors.push('Opis musi mieć od 50 do 5000 znaków');
    }

    if (!info.address?.city || !info.address?.postalCode) {
      errors.push('Wymagane: miejscowość i kod pocztowy');
    }

    if (!info.images || info.images.length < WIZARD_LIMITS.PROPERTY_MIN_IMAGES || info.images.length > WIZARD_LIMITS.PROPERTY_MAX_IMAGES) {
      errors.push(`Wymagane od ${WIZARD_LIMITS.PROPERTY_MIN_IMAGES} do ${WIZARD_LIMITS.PROPERTY_MAX_IMAGES} zdjęć`);
    }

    // Walidacja kontaktów: format + wymagalność gdy toggle jest włączony
    const phonePattern = /^\+?[0-9\s\-()]{6,20}$/;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (info.contactVisibility) {
      if (info.contactVisibility.showPhone && !info.phone?.trim()) {
        errors.push('Numer telefonu jest wymagany gdy włączono wyświetlanie telefonu');
      }
      if (info.contactVisibility.showWhatsApp && !info.whatsApp?.trim()) {
        errors.push('Numer WhatsApp jest wymagany gdy włączono wyświetlanie WhatsApp');
      }
      if (info.contactVisibility.showEmail && !info.email?.trim()) {
        errors.push('Email jest wymagany gdy włączono wyświetlanie emaila');
      }
    }

    // Walidacja formatu (niezależnie od toggle)
    if (info.phone?.trim() && !phonePattern.test(info.phone.trim())) {
      errors.push('Nieprawidłowy format numeru telefonu');
    }
    if (info.whatsApp?.trim() && !phonePattern.test(info.whatsApp.trim())) {
      errors.push('Nieprawidłowy format numeru WhatsApp');
    }
    if (info.email?.trim() && !emailPattern.test(info.email.trim())) {
      errors.push('Nieprawidłowy format adresu email');
    }

    return { isValid: errors.length === 0, errors };
  }

  validateUnitTypes(unitTypes: UnitType[]): ValidationResult {
    const errors: string[] = [];

    if (!unitTypes || unitTypes.length < 1) {
      errors.push('Wymagany jest minimum 1 typ jednostki');
    }

    if (unitTypes.length > WIZARD_LIMITS.MAX_UNIT_TYPES) {
      errors.push(`Maksymalnie można dodać ${WIZARD_LIMITS.MAX_UNIT_TYPES} typów jednostek`);
    }

    // Global image limit check
    const totalImages = unitTypes.reduce((sum, ut) => sum + (ut.images?.length || 0), 0);
    if (totalImages > WIZARD_LIMITS.GLOBAL_MAX_IMAGES) {
      errors.push(`Łączna liczba zdjęć (${totalImages}) przekracza limit ${WIZARD_LIMITS.GLOBAL_MAX_IMAGES}`);
    }

    unitTypes.forEach((unit, index) => {
      const unitErrors = this.validateSingleUnitType(unit);
      if (unitErrors.length > 0) {
        errors.push(`Typ "${unit.name || index + 1}": ${unitErrors.join(', ')}`);
      }
    });

    return { isValid: errors.length === 0, errors };
  }

  validateSingleUnitType(unit: UnitType): string[] {
    const errors: string[] = [];

    if (!unit.propertyTypeId) {
      errors.push('Typ obiektu jest wymagany');
    }

    if (!unit.name || unit.name.length < 3 || unit.name.length > 50) {
      errors.push('Nazwa musi mieć 3-50 znaków');
    }

    if (!unit.images || unit.images.length < WIZARD_LIMITS.UNIT_TYPE_MIN_IMAGES || unit.images.length > WIZARD_LIMITS.UNIT_TYPE_MAX_IMAGES) {
      errors.push(`Wymagane ${WIZARD_LIMITS.UNIT_TYPE_MIN_IMAGES}-${WIZARD_LIMITS.UNIT_TYPE_MAX_IMAGES} zdjęć`);
    } else if (unit.images.some(img => img.isUploading)) {
      errors.push('Poczekaj na zakończenie wgrywania zdjęć');
    }

    if (!unit.quantity || unit.quantity < 1 || unit.quantity > 100) {
      errors.push('Ilość: 1-100');
    }

    if (!unit.maxOccupancy || unit.maxOccupancy < 1 || unit.maxOccupancy > 20) {
      errors.push('Maks. osób: 1-20');
    }

    if (!unit.unitNames || unit.unitNames.length !== unit.quantity) {
      errors.push('Nazwy jednostek są wymagane');
    } else if (unit.unitNames.some(n => !n || !n.trim())) {
      errors.push('Wszystkie nazwy jednostek muszą być wypełnione');
    }

    return errors;
  }

  getUnitTypeErrorsMap(unitTypes: UnitType[]): UnitTypeValidationMap {
    const map: UnitTypeValidationMap = {};
    for (const unit of unitTypes) {
      const errors = this.validateSingleUnitType(unit);
      if (errors.length > 0) {
        map[unit.id] = errors;
      }
    }
    return map;
  }

  validatePricingRules(rules: PricingRules | null): ValidationResult {
    const errors: string[] = [];

    if (!rules) {
      return { isValid: false, errors: ['Zasady cenowe są wymagane'] };
    }

    if (!rules.electricityPolicy) {
      errors.push('Polityka prądu jest wymagana');
    }

    if (!rules.petPolicy) {
      errors.push('Polityka zwierząt jest wymagana');
    }

    if ((rules.petPolicy === 'allowed_paid_per_day' || rules.petPolicy === 'allowed_paid_per_stay') && (!rules.petFee || rules.petFee <= 0)) {
      errors.push('Opłata za zwierzę jest wymagana gdy zwierzęta są płatne');
    }

    if (rules.depositPercentage === undefined || rules.depositPercentage < 0 || rules.depositPercentage > 100) {
      errors.push('Zadatek musi być od 0 do 100%');
    }

    if (!rules.checkInTime) {
      errors.push('Godzina zameldowania jest wymagana');
    }

    if (!rules.checkOutTime) {
      errors.push('Godzina wymeldowania jest wymagana');
    }

    if (!rules.minStay || rules.minStay < 1) {
      errors.push('Minimalny pobyt musi być co najmniej 1 dzień');
    }

    return { isValid: errors.length === 0, errors };
  }

  validateSeasons(seasons: Season[], unitTypeIds: string[]): ValidationResult {
    const errors: string[] = [];

    for (const season of seasons) {
      if (!season.name) {
        errors.push(`Sezon wymaga nazwy`);
      }

      if (!season.startDate || !season.endDate) {
        errors.push(`Sezon "${season.name}": wymagane daty`);
      }

      if (season.startDate && season.endDate && season.startDate >= season.endDate) {
        errors.push(`Sezon "${season.name}": data początku musi być przed datą końca`);
      }

      if (!season.color) {
        errors.push(`Sezon "${season.name}": wymagany kolor`);
      }

      const missingPrices = unitTypeIds.filter(
        utId => !season.unitPrices.some(up => up.unitTypeId === utId)
      );
      if (missingPrices.length > 0) {
        errors.push(`Sezon "${season.name}": brakuje cen dla niektórych typów jednostek`);
      }
    }

    const overlaps = this.findSeasonOverlaps(seasons);
    if (overlaps.length > 0) {
      overlaps.forEach(overlap => {
        errors.push(`Nakładające się sezony: "${overlap.season1}" i "${overlap.season2}"`);
      });
    }

    return { isValid: errors.length === 0, errors };
  }

  findSeasonOverlaps(seasons: Season[]): Array<{ season1: string; season2: string }> {
    const overlaps: Array<{ season1: string; season2: string }> = [];

    for (let i = 0; i < seasons.length; i++) {
      for (let j = i + 1; j < seasons.length; j++) {
        if (this.doSeasonsOverlap(seasons[i], seasons[j])) {
          overlaps.push({
            season1: seasons[i].name,
            season2: seasons[j].name
          });
        }
      }
    }

    return overlaps;
  }

  private doSeasonsOverlap(season1: Season, season2: Season): boolean {
    if (!season1.startDate || !season1.endDate || !season2.startDate || !season2.endDate) {
      return false;
    }
    return season1.startDate <= season2.endDate && season2.startDate <= season1.endDate;
  }
}
